import { EpgCategory } from './EpgCategory';
import { EpgChannel } from './EpgChannel';
import { EpgProgramGenre } from './EpgProgramGenre';
import {Consts, ICategory, ProgramState} from './index';
import { Observer } from './Observer';

export interface ITypeProgramsCollection {
  [key: number]: { programs: EpgProgram[]; isExtended: boolean };
}

export class EpgProgram {
  private _id: number;
  private _external_id: number;
  private _title: string;
  private _startTimestamp: number;
  private _endTimestamp: number;
  private _objectId: number;
  private _virtualObjectId: string;
  private _state: ProgramState = ProgramState.FUTURE;
  private _channel: EpgChannel;
  private _duration: number;
  private _startDate: Date;
  private _endDate: Date;
  private _virtualStream: string | object;
  // flag that indicates when program started playing from program panel click
  private _clickedFromProgramsPanel: boolean = false;
  private _groupedPrograms: object | null;
  private _scheduleType: string;
  private _category: ICategory;

  public constructor(data?: any) {
    if (data) {
      this.setData(data);
    }
  }

  public setData(data: any) {
    this._id = data.id || -1;
    this._external_id = data.external_id || -1;
    this._title = data.title;
    this._startTimestamp = data.start_timestamp;
    this._endTimestamp = data.end_timestamp;
    this._objectId = data.object_id || -1;
    this._virtualObjectId = data.virtual_object_id || '';
    this._duration = this._endTimestamp - this._startTimestamp;
    this._startDate = new Date(this._startTimestamp * 1000);
    this._endDate = new Date(this._endTimestamp * 1000);
    this._groupedPrograms = data.grouped_programs || null;
    this._scheduleType = data.schedule_type || 'single';
    this._category = data?.category || null;
  }

  public checkState(timestamp: number): ProgramState {
    if (this._endTimestamp <= timestamp) {
      this._state = ProgramState.PAST;
    } else if (
      this._startTimestamp <= timestamp &&
      this._endTimestamp > timestamp
    ) {
      this._state = ProgramState.CURRENT;
    } else if (this._startTimestamp > timestamp) {
      this._state = ProgramState.FUTURE;
    }
    return this._state;
  }

  public isEnding(timestamp: number, timeOffset: number): boolean {
    let result: boolean = false;
    if (timestamp) {
      result = this._endTimestamp - timestamp <= timeOffset;
    }
    return result;
  }

  public getTitleByLength(length: number): string {
    return this._title.length > length
      ? this._title.substr(0, length) + '...'
      : this._title;
  }

  get id(): number {
    return this._id;
  }

  get external_id(): number {
    return this._external_id;
  }

  get title(): string {
    return this._title;
  }

  get startTimestamp(): number {
    return this._startTimestamp;
  }

  get endTimestamp(): number {
    return this._endTimestamp;
  }

  get objectId(): number {
    return this._objectId;
  }

  get virtualObjectId(): string {
    return this._virtualObjectId;
  }

  get state(): ProgramState {
    return this._state;
  }

  get endDate(): Date {
    return this._endDate;
  }
  get startDate(): Date {
    return this._startDate;
  }
  get duration(): number {
    return this._duration;
  }
  set channel(value: EpgChannel) {
    this._channel = value;
  }
  get channel(): EpgChannel {
    return this._channel;
  }

  get category(): ICategory {
    return this._category;
  }

  set virtualStream(value: string | any) {
    if (!this._virtualStream) {
      this._virtualStream = value;
      Observer.emit(Consts.EVENT_PROGRAM_VIRTUAL_STREAM_UPDATED, this);
    }
  }

  get virtualStream(): any {
    return this._virtualStream;
  }

  get isActive() {
    return this.channel.activeProgram === this;
  }

  set clickedFromProgramsPanel(value: boolean) {
    this._clickedFromProgramsPanel = value;
  }

  get clickedFromProgramsPanel(): boolean {
    return this._clickedFromProgramsPanel;
  }

  get groupedPrograms(): object | null {
    return this._groupedPrograms;
  }

  get scheduleType(): string {
    return this._scheduleType;
  }

  get startString() {
    return this._startDate ? this._startDate.toString() : '';
  }

  get endString() {
    return this._endDate ? this._endDate.toString() : '';
  }
}
